import { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

interface Item {
  id: string;
  nome: string;
}

interface TableProps<T extends Item> {
  loadData: () => Promise<T[]>;
  titulo: string;
  createItem: (item: T) => Promise<void>;
  updateItem: (item: T) => Promise<T>;
  deleteItem: (id: string) => Promise<void>;
}

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-root': {
    border: 'none',
  },
  '& .MuiDataGrid-cell': {
    color: theme.palette.text.primary,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: theme.palette.grey[100],
  },
}));

export default function Table<T extends Item>({
  loadData,
  titulo,
  createItem,
  updateItem,
  deleteItem,
}: TableProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [itemName, setItemName] = useState('');

  const loadAllData = async () => {
    setLoading(true);
    try {
      const result = await loadData();
      setData(result);
    } catch (error) {
      console.error(`Erro ao carregar ${titulo}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setItemName('');
    setOpenDialog(true);
  };

  const handleEdit = (item: T) => {
    setDialogMode('edit');
    setSelectedItem(item);
    setItemName(item.nome);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSave = async () => {
    if (dialogMode === 'create') {
      const newItem: T = { id: `${Date.now()}`, nome: itemName } as T; // Exemplo simples
      await createItem(newItem);
    } else if (dialogMode === 'edit' && selectedItem) {
      const updatedItem = { ...selectedItem, nome: itemName } as T;
      await updateItem(updatedItem);
    }
    await loadAllData();
    handleDialogClose();
  };

  const handleDelete = async (id: string) => {
    await deleteItem(id);
    await loadAllData();
  };

  const columns: GridColDef[] = [
    { field: 'nome', headerName: 'Nome', flex: 1 },
    {
      field: 'actions',
      headerName: 'Ações',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit({ id: params.id as string, nome: params.row.nome } as T)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Deletar"
          onClick={() => handleDelete(params.id as string)}
          showInMenu
        />,
      ],
    },
  ];

  useEffect(() => {
    loadAllData();
  }, [loadData, titulo]);

  return (
    <Paper sx={{ margin: 2 }}>
      <Button
        startIcon={<AddCircleIcon />}
        onClick={handleCreate}
        sx={{ marginY: 2, marginLeft: 2 }}
      >
        Adicionar {titulo}
      </Button>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <StyledDataGrid
          rowSelection={false}
          rows={data}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[15, 25, 50]}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        />
      )}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        sx={{ '& .MuiDialog-paper': { width: '500px', maxWidth: '500px' } }} // Ajuste a largura conforme necessário
      >
        <DialogTitle>{dialogMode === 'create' ? `Adicionar ${titulo}` : `Editar ${titulo}`}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            variant="outlined"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
